import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { inboundCallsAPI, extractErrorMessage } from '../../services/api';
import { formatPhoneNumber, formatDuration } from '../../utils/formatting';
import type { InboundCall } from '../../types';

const KNOWN_STATUSES = ['answered', 'voicemail', 'busy', 'no_answer'];
const KNOWN_TEMPERATURES = ['hot', 'warm', 'lukewarm', 'cold', 'dead'];
const FETCH_BATCH_SIZE = 100;

const normalize = (value?: string | null): string => (value ?? '').trim().toLowerCase();

export function useInboundCallsPage() {
  const navigate = useNavigate();
  const [allCalls, setAllCalls] = useState<InboundCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phoneFilter, setPhoneFilter] = useState('');
  const [tempFilter, setTempFilter] = useState<string[]>([]);
  const [dispositionFilter, setDispositionFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [goalFilter, setGoalFilter] = useState<string[]>([]);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    loadInboundCalls();
  }, []);

  const loadInboundCalls = async () => {
    try {
      setLoading(true);
      setError(null);

      let skip = 0;
      const fetchedCalls: InboundCall[] = [];

      while (true) {
        const batch = await inboundCallsAPI.getCalls(skip, FETCH_BATCH_SIZE);
        fetchedCalls.push(...batch);
        if (batch.length < FETCH_BATCH_SIZE) {
          break;
        }
        skip += FETCH_BATCH_SIZE;
      }

      setAllCalls(fetchedCalls);
    } catch (err: any) {
      console.error('Error loading inbound calls:', err);
      const status = err?.response?.status;
      if (status === 403) {
        setError('Inbound is not enabled for your current plan.');
      } else {
        setError(extractErrorMessage(err, 'Failed to load inbound calls'));
      }
    } finally {
      setLoading(false);
    }
  };

  const temperatureOptions = KNOWN_TEMPERATURES.map((value) => ({
    value,
    label: value.replace(/_/g, ' '),
  }));

  const dispositionOptions = useMemo(() => Array.from(new Set(
    allCalls
      .map((call) => normalize(call.disposition))
      .filter(Boolean)
  )).sort().map((value) => ({
    value,
    label: value.replace(/_/g, ' '),
  })), [allCalls]);

  const statusOptions = useMemo(() => {
    const dynamicStatuses = allCalls.map((call) => normalize(call.call_status)).filter(Boolean);
    const allStatuses = Array.from(new Set([...KNOWN_STATUSES, ...dynamicStatuses])).sort();
    return allStatuses.map((value) => ({
      value,
      label: value.replace(/_/g, ' '),
    }));
  }, [allCalls]);

  const goalOptions = useMemo(() => Array.from(new Set(
    allCalls
      .map((call) => normalize(call.goal_outcome))
      .filter(Boolean)
  )).sort().map((value) => ({
    value,
    label: value.replace(/_/g, ' '),
  })), [allCalls]);

  const filteredCalls = useMemo(() => {
    const phoneQuery = normalize(phoneFilter);
    return allCalls.filter((call) => {
      const callTemperature = normalize(call.lead_temperature);
      const callDisposition = normalize(call.disposition);
      const callStatus = normalize(call.call_status);
      const callGoal = normalize(call.goal_outcome);

      if (tempFilter.length > 0 && !tempFilter.includes(callTemperature)) return false;
      if (dispositionFilter.length > 0 && !dispositionFilter.includes(callDisposition)) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(callStatus)) return false;
      if (goalFilter.length > 0 && !goalFilter.includes(callGoal)) return false;

      if (phoneQuery) {
        const haystack = `${call.caller_phone} ${call.caller_name ?? ''}`.toLowerCase();
        if (!haystack.includes(phoneQuery)) return false;
      }

      return true;
    });
  }, [allCalls, dispositionFilter, goalFilter, phoneFilter, statusFilter, tempFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCalls.length / itemsPerPage));
  const calls = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredCalls.slice(start, start + itemsPerPage);
  }, [currentPage, filteredCalls]);

  useEffect(() => {
    setCurrentPage(0);
  }, [phoneFilter, tempFilter, dispositionFilter, statusFilter, goalFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, totalPages]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'answered':
        return 'bg-gradient-to-r from-green-500/20 to-green-600/20 text-green-400 border border-green-500/30';
      case 'voicemail':
        return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 text-yellow-400 border border-yellow-500/30';
      case 'busy':
        return 'bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 border border-red-500/30';
      case 'no_answer':
        return 'bg-gradient-to-r from-slate-500/20 to-slate-600/20 text-slate-400 border border-slate-500/30';
      default:
        return 'bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 border border-blue-500/30';
    }
  };

  // LT-477 + LT-487: every inbound row is navigable. Calls with a
  // persisted CallSummary use that id (full analysis view); calls
  // without one (no-answer / voicemail / busy, or analyzer hasn't run
  // yet) fall back to the InboundCallLog id — GET /calls/{id} accepts
  // either and synthesises a partial response so the detail page still
  // renders basic metadata + any captured transcript.
  const openCallDetails = (call: InboundCall) => {
    const target = call.call_summary_id ?? call.id;
    if (target) {
      navigate(`/calls/${target}`);
    }
  };

  const canViewDetails = (call: InboundCall) => !!(call.call_summary_id ?? call.id);

  const handlePrevPage = () => setCurrentPage((p) => Math.max(0, p - 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages - 1, p + 1));

  return {
    calls,
    totalCalls: filteredCalls.length,
    loading,
    error,
    phoneFilter,
    setPhoneFilter,
    tempFilter,
    setTempFilter,
    dispositionFilter,
    setDispositionFilter,
    statusFilter,
    setStatusFilter,
    goalFilter,
    setGoalFilter,
    temperatureOptions,
    dispositionOptions,
    statusOptions,
    goalOptions,
    currentPage,
    totalPages,
    itemsPerPage,
    formatPhoneNumber,
    formatDuration,
    getStatusColor,
    openCallDetails,
    canViewDetails,
    handlePrevPage,
    handleNextPage,
    navigate,
  };
}
